import makeStyles from '@material-ui/core/styles/makeStyles'
import { Sell, SellOutlined } from '@mui/icons-material'
import MemberFavoriteButton from 'photo-module/photo/ui/photo-viewer/actions-bar/MemberFavoriteButton'
import DeleteButton from 'photo-module/photo/ui/photo-viewer/actions-bar/DeleteButton'
import BackButton from 'photo-module/photo/ui/photo-viewer/actions-bar/BackButton'
import SpypointVideoHdButton from '../../../video-module/core/SpypointVideoHdButton'
import PhotoHdButton from './photo-viewer/full-hd-status/PhotoHdButton'
import useSelectedPhoto from 'photo-module/photo/core/useSelectedPhoto'
import videoState from 'video-module/video-clip/core/videoState'
import { useLDFlags } from 'spypoint/src/launchDarkly-module/launchDarklyFlagsContext'
import { Button } from '@material-ui/core'
import downloadStatus from 'photo-module/download/downloadStatus'
import { useHistory } from 'react-router-dom'

export const useStyles = makeStyles(theme => ({
  margin: {
    margin: theme.spacing(1),
  },
  viewerActionsBar: {
    position: 'absolute',
    top: '0px',
    backgroundColor: theme.palette.background?.viewer,
    width: '100%',
    height: '78px',
    display: 'flex',
    flexWrap: 'nowrap',
    alignItems: 'center',
    justifyContent: 'space-between',
    zIndex: 999,
  },
  backActionContainer: {
    marginLeft: '20px',
  },
  photoStatusActionsContainer: ({ hasNonRequestableState }) => ({
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'nowrap',
    [theme.breakpoints.up('sm')]: {
      marginRight: '20px',
    },
    [theme.breakpoints.down('sm')]: {
      marginRight: hasNonRequestableState ? '4px' : '20px', // Further reduced right margin on mobile
    },
    '& > *': {
      marginLeft: hasNonRequestableState
        ? theme.spacing(0.25) // Very minimal spacing on mobile when download state is not requestable
        : theme.spacing(1),
      [theme.breakpoints.up('sm')]: {
        marginLeft: theme.spacing(1), // Normal spacing on desktop
      },
    },
    '& svg': {
      '& path': {
        fill: theme.palette.secondary.contrastText,
      },
      height: hasNonRequestableState ? '20px' : '25px', // Smaller icons on mobile when spacing is reduced
      width: 'auto',
      [theme.breakpoints.up('sm')]: {
        height: '25px', // Normal size on desktop
      },
    },
    // Ensure buttons stay on same line on mobile
    [theme.breakpoints.down('sm')]: {
      '& > *': {
        minWidth: hasNonRequestableState ? '32px' : 'auto', // Minimum width to prevent wrapping
        '& svg': {
          fontSize: hasNonRequestableState ? '18px' : '25px', // Smaller icons on mobile
        },
      },
    },
  }),
  tagButton: ({ hasNonRequestableState }) => ({
    minWidth: 'auto',
    padding: hasNonRequestableState
      ? `${theme.spacing(0.5)}px ${theme.spacing(1.5)}px`
      : theme.spacing(1.3),
    '& svg': {
      fontSize: hasNonRequestableState ? '20px' : '25px',
      [theme.breakpoints.up('sm')]: {
        fontSize: '25px',
      },
    },
  }),
  disabledActionButton: {
    opacity: '0.3 !important',
    cursor: 'not-allowed !important',
    pointerEvents: 'none !important',
    '& .MuiIconButton-root': {
      '&:hover': {
        backgroundColor: 'transparent !important',
      },
      '&.Mui-disabled': {
        color: 'inherit !important',
        opacity: '1 !important',
      },
    },
    '& .MuiButton-root': {
      '&:hover': {
        backgroundColor: 'transparent !important',
      },
      '&.Mui-disabled': {
        color: 'inherit !important',
        opacity: '1 !important',
        backgroundColor: 'transparent !important',
      },
    },
    '& svg': {
      opacity: '1 !important',
      '& path': {
        fill: 'currentColor !important',
      },
    },
  },
  disabledTagButton: {
    opacity: '0.6 !important',
    cursor: 'not-allowed !important',
    pointerEvents: 'none !important',
    '& .MuiButton-root': {
      '&:hover': {
        backgroundColor: 'transparent !important',
      },
      '&.Mui-disabled': {
        color: 'inherit !important',
        opacity: '1 !important',
        backgroundColor: 'transparent !important',
      },
    },
    '& svg': {
      opacity: '1 !important',
      '& path': {
        fill: 'currentColor !important',
      },
    },
  },
}))

const SpypointViewerActionsBar = () => {
  const { flags } = useLDFlags()
  const isMediaTagsEnabled = flags && flags['media-tags']
  const photo = useSelectedPhoto()
  const history = useHistory()

  // Check if any download state is different from requestable
  const hasNonRequestableState = photo && (
    photo.hdStatus !== downloadStatus.requestable ||
    photo.hdVideoStatus !== downloadStatus.requestable ||
    photo.sdVideoStatus !== downloadStatus.requestable
  )

  // Check if photo is shared (determines if buttons should be disabled)
  const shared = JSON.parse(localStorage.getItem('isShared'))

  const classes = useStyles({ hasNonRequestableState })

  const handleTagButtonClick = () => {
    if (photo) {
      // Navigate to preview page with modal ready to open
      history.push(`/camera/${photo.cameraId}/photo/${photo.id}/preview?openTagModal=true`)
    }
  }

  return (
    <div className={classes.viewerActionsBar}>
      <div className={classes.backActionContainer}>
        <BackButton />
      </div>
      <div className={classes.photoStatusActionsContainer}>
        { !!isMediaTagsEnabled && (
          <Button
            className={`${classes.tagButton} ${shared ? classes.disabledTagButton : ''}`}
            onClick={handleTagButtonClick}
            disabled={shared}
          >
            { !photo.tags?.length
              ? <SellOutlined />
              : <Sell /> }
          </Button>
        ) }
        <div className={shared ? classes.disabledActionButton : ''}>
          { videoState.hasPreview(photo) ? <SpypointVideoHdButton photo={photo} hideText /> : <PhotoHdButton /> }
        </div>
        <div className={shared ? classes.disabledActionButton : ''}>
          <MemberFavoriteButton />
        </div>
        <DeleteButton />
      </div>
    </div>
  )
}

export default SpypointViewerActionsBar
